import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";

/**
 * Pingt alle 3s die Phase-API; wenn die letzte erfolgreiche Antwort
 * länger als `staleAfterMs` zurückliegt, ist der Status "offline".
 *
 * Der ReconnectBanner blendet sich auf Basis dieses Hooks ein.
 * Brief 06: "Backend off ≥6s → Banner erscheint".
 */

interface ConnectionStatus {
  online: boolean;
  /** Anzahl konsekutiver Fehlversuche. */
  retries: number;
}

export function useConnectionStatus(staleAfterMs = 6_000): ConnectionStatus {
  const { state } = useStore();
  const token = state.auth.token;
  const [status, setStatus] = useState<ConnectionStatus>({
    online: true,
    retries: 0,
  });
  const lastSuccessRef = useRef<number>(Date.now());
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!token) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let retries = 0;

    const tick = async () => {
      if (cancelled.current) return;
      try {
        await api.phase.get(token);
        if (cancelled.current) return;
        lastSuccessRef.current = Date.now();
        retries = 0;
        setStatus({ online: true, retries: 0 });
      } catch {
        retries += 1;
        const stale = Date.now() - lastSuccessRef.current >= staleAfterMs;
        if (!cancelled.current) {
          setStatus({ online: !stale, retries });
        }
      }
      if (!cancelled.current) timer = setTimeout(tick, 3_000);
    };

    tick();

    return () => {
      cancelled.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, staleAfterMs]);

  return status;
}
